import PropTypes from 'prop-types';
import { numberFormat, unitToString } from '@/utils/unitUtils';

const formatQuantity = (quantity, digits = 2) => {
  if (!quantity || quantity.scalar == null) return null;
  return `${numberFormat(quantity.scalar, digits)} ${unitToString(quantity.unit) || ''}`.trim();
};

const formatFactor = factor => {
  if (factor == null) return null;
  return `× ${numberFormat(factor, factor < 10 ? 3 : 1)}`;
};

// The scaling multiplier carries units (e.g. plant capacity in kWe, or annual
// energy in MWh/year): it's the factor that turns a normalized per-unit cost
// into an absolute cost. A dimensionless multiplier of 1 means no scaling.
const formatMultiplier = quantity => {
  if (!quantity || quantity.scalar == null) return null;
  const unit = unitToString(quantity.unit) || '';
  if (!unit && Math.abs(quantity.scalar - 1) < 1e-9) return null;
  return unit ? `× ${numberFormat(quantity.scalar, 2)} ${unit}` : `× ${numberFormat(quantity.scalar, 3)}`;
};

const ArrowCell = ({ text, hint }) => (
  <td className="px-3 py-2 text-xs text-gray-500 align-middle whitespace-nowrap" title={hint || undefined}>
    <span className="inline-flex items-center gap-1">
      <span className="text-gray-400">→</span>
      {text != null ? <span className="tabular-nums">{text}</span> : <span className="text-gray-300">—</span>}
    </span>
  </td>
);

ArrowCell.propTypes = {
  text: PropTypes.string,
  hint: PropTypes.string,
};

const ValueCell = ({ value, emphasize }) => {
  if (value == null) {
    return <td className="px-3 py-2 text-sm tabular-nums text-gray-400">—</td>;
  }
  return (
    <td
      className={`px-3 py-2 text-sm tabular-nums whitespace-nowrap ${
        emphasize ? 'font-semibold text-gray-900' : 'text-gray-800'
      }`}
    >
      {value}
    </td>
  );
};

ValueCell.propTypes = {
  value: PropTypes.string,
  emphasize: PropTypes.bool,
};

const CostTransformations = ({ transformations }) => {
  if (!transformations || !transformations.rows || transformations.rows.length === 0) {
    return null;
  }

  const {
    rows,
    cost_reference_year: costRefYear,
    analysis_start_year: analysisYear,
    inflation_rate: inflationRate,
    inflation_factor: inflationFactor,
    plant_capacity: plantCapacity,
    reference_capacity: referenceCapacity,
    scaling_exponent: scalingExponent,
    scaling_factor: scalingFactor,
  } = transformations;

  const inflationHint =
    costRefYear !== analysisYear && inflationRate
      ? `(1 + ${numberFormat(inflationRate.scalar / 100, 4)}) ^ (${analysisYear} − ${costRefYear})`
      : '';
  const usesExponentialScaling = scalingFactor != null && referenceCapacity && plantCapacity && scalingExponent;
  const scalingHint = usesExponentialScaling
    ? `Per-unit cost × capacity, scaled by (${numberFormat(plantCapacity.scalar, 2)} / ${numberFormat(referenceCapacity.scalar, 2)}) ^ ${numberFormat(scalingExponent.scalar, 2)} = × ${numberFormat(scalingFactor, 3)}`
    : 'Per-unit cost × plant capacity';

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
            <th className="py-2 pr-3 text-left font-normal">Cost</th>
            <th className="py-2 px-3 text-left font-normal">Module input</th>
            <th className="py-2 px-3 text-left font-normal">Capacity scaling</th>
            <th className="py-2 px-3 text-left font-normal">Inflation to {analysisYear}</th>
            <th className="py-2 pl-3 text-left font-normal">Used in DCFA</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            // When a cost is power-law-scaled, show the clean dimensionless
            // factor (e.g. × 128); otherwise show the absolute capacity /
            // throughput multiplier (e.g. × plant capacity).
            const usesPowerLaw = row.power_law_factor != null;
            const scalingText = usesPowerLaw
              ? formatFactor(row.power_law_factor)
              : formatMultiplier(row.scaling_multiplier);
            const inflationText = row.inflated ? formatFactor(inflationFactor) : null;
            return (
              <tr key={row.label} className="border-b border-gray-50">
                <td className="py-2 pr-3 text-sm text-gray-700 whitespace-nowrap">{row.label}</td>
                <ValueCell value={formatQuantity(row.input_value)} />
                <ArrowCell text={scalingText} hint={usesPowerLaw ? scalingHint : undefined} />
                <ArrowCell text={inflationText} hint={inflationText ? inflationHint : undefined} />
                <ValueCell value={formatQuantity(row.final_value)} emphasize />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

CostTransformations.propTypes = {
  transformations: PropTypes.shape({
    rows: PropTypes.array,
    cost_reference_year: PropTypes.number,
    analysis_start_year: PropTypes.number,
    inflation_rate: PropTypes.object,
    inflation_factor: PropTypes.number,
    plant_capacity: PropTypes.object,
    reference_capacity: PropTypes.object,
    scaling_exponent: PropTypes.object,
    scaling_factor: PropTypes.number,
  }),
};

export default CostTransformations;
